import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import { toast } from 'react-hot-toast';

import {
  API_URL,
  getOutlookEmailsFromDB,
  getGmailEmailsFromDB,
  getAuthenticatedApps,
} from '../services/api';
import { DataState } from './DataProvider';
import { useWebSocket } from './WebSocketProvider';

const EmailsContext = createContext();

export const EmailsProvider = ({ children }) => {
  const { userGoogleId } = DataState();
  const [emails, setEmails] = useState([]);
  const [totalEmails, setTotalEmails] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [emailfilter, setEmailFilter] = useState({ Inbox: 'Inbox' });
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [originalEmailsOrder, setOriginalEmailsOrder] = useState([]);
  const [emailStats, setEmailStats] = useState({});
  const [isOutlook, setIsOutlook] = useState(true); // Toggle variable for Gmail/Outlook
  const [isGmailAuthenticated, setIsGmailAuthenticated] = useState(false);
  const [isOutlookAuthenticated, setIsOutlookAuthenticated] = useState(false);
  const [isGmailSynchronized, setIsGmailSynchronized] = useState(false);
  const [isOutlookSynchronized, setIsOutlookSynchronized] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSearchDone, setIsSearchDone] = useState(0);
  const [newEmailCount, setNewEmailCount] = useState(0);
  const [priorities, setPriorities] = useState([]);
  const seenMessageIdsRef = useRef(new Set());

  const [gmailClearStatus, setGmailClearStatus] = useState({
    status: false,
    state: 0,
  });
  const [outlookClearStatus, setOutlookClearStatus] = useState({
    status: false,
    state: 0,
  });

  const [gmailSyncStatus, setGmailSyncStatus] = useState({
    status: false,
    state: 0,
  });
  const [outlookSyncStatus, setOutlookSyncStatus] = useState({
    status: false,
    state: 0,
  });

  const [gmailAuthStatus, setGmailAuthStatus] = useState({
    status: false,
    state: 0,
  });
  const [outlookAuthStatus, setOutlookAuthStatus] = useState({
    status: false,
    state: 0,
  });

  const [forceRefetch, setForceRefetch] = useState(false);
  const [refreshDialogKey, setRefreshDialogKey] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const { subscribe, isConnected } = useWebSocket();
  // Handling notifications
  const [refreshKey, setRefreshKey] = useState(0);

  const emailsPerPage = 20;
  // const totalPages = Math.ceil(totalEmails / emailsPerPage);
  useEffect(() => {
    const total_Pages = Math.ceil(totalEmails / emailsPerPage);
    setTotalPages(total_Pages);
  }, [totalEmails]);

  // TTL of 10 minutes and maximum of 10 keys.
  const TTL = 10 * 60 * 1000;
  const MAX_KEYS = 10;

  // Using a Map to facilitate LRU behavior.
  const emailCacheRef = useRef(new Map());

  useEffect(() => {
    // Only run this logic once when either sync flag is set (initial sync preference)
    if (isGmailSynchronized && !isOutlookSynchronized) {
      // If Gmail is synced, prefer it. Otherwise, fallback to Outlook.
      setIsOutlook(false);
    } else if (isOutlookSynchronized && !isGmailSynchronized) {
      setIsOutlook(true);
    }
  }, [isGmailSynchronized, isOutlookSynchronized]);

  useEffect(() => {
    if (gmailSyncStatus.status) {
      setIsGmailSynchronized(true);
    } else if (!gmailSyncStatus.status) {
      setIsGmailSynchronized(false);
    }
    if (outlookSyncStatus.status) {
      setIsOutlookSynchronized(true);
    } else if (!outlookSyncStatus.status) {
      setIsOutlookSynchronized(false);
    }
    setRefreshKey((k) => k + 1);
    // eslint-disable-next-line
  }, [gmailSyncStatus.state, outlookSyncStatus.state]);

  const fetchAuthenticatedApps = async () => {
    let data;
    try {
      const response = await getAuthenticatedApps(userGoogleId);
      if (response?.apps?.length > 0) {
        if (response.apps.includes('gmail')) {
          setIsGmailAuthenticated(true);

          const response = await fetch(`${API_URL}/auth/gmail/getCounts`, {
            method: 'GET',
            headers: { Authorization: `Bearer ${userGoogleId}` },
            credentials: 'include',
          });
          data = await response.json();
          const emailCount = parseInt(data.emails, 10);

          // eslint-disable-next-line
          if (emailCount != 0) {
            setIsGmailSynchronized(true);
          }
        }
        if (response.apps.includes('microsoft_outlook')) {
          setIsOutlookAuthenticated(true);
          // const outlookRes = await getAppResources("microsoft_outlook", userGoogleId);
          // setIsOutlookSynchronized(outlookRes?.is_synchronized || false);
          const response = await fetch(`${API_URL}/auth/outlook/getCounts`, {
            method: 'GET',
            headers: { Authorization: `Bearer ${userGoogleId}` },
            credentials: 'include',
          });
          data = await response.json();
          const emailCount = parseInt(data.emails, 10);
          if (emailCount > 0) {
            setIsOutlookSynchronized(true);
          }
        }
      }
      setRefreshKey((k) => k + 1);
    } catch (authErr) {
      toast.error('Error fetching auth apps: ' + authErr.message);
    }
  };

  useEffect(() => {
    fetchAuthenticatedApps();
    // eslint-disable-next-line
  }, [userGoogleId, isOutlookAuthenticated, isGmailAuthenticated]);

  useEffect(() => {
    let isActive = true;
    setLoadingEmails(true);
    const now = Date.now();
    const cacheKey = JSON.stringify({
      filter: emailfilter,
      page: currentPage,
      isOutlook,
    });

    // Check the cache.
    const cachedEntry = emailCacheRef.current.get(cacheKey);
    if (cachedEntry && now - cachedEntry.timestamp < TTL && !forceRefetch) {
      // Update recency: remove and reinsert with a fresh timestamp.
      emailCacheRef.current.delete(cacheKey);
      emailCacheRef.current.set(cacheKey, {
        data: cachedEntry.data,
        timestamp: now,
      });
      const { emails, totalEmails, emailStats } = cachedEntry.data;
      if (isActive) {
        setEmails(emails);
        setOriginalEmailsOrder(emails);
        setEmailStats(emailStats);
        setTotalEmails(totalEmails);
        setLoadingEmails(false);
      }
      return;
    } else if (cachedEntry && !forceRefetch) {
      // If the entry is expired, remove it.
      emailCacheRef.current.delete(cacheKey);
    }
    setForceRefetch(false);

    // Fetch new data if no valid cached data exists.
    const fetchEmails = async () => {
      try {
        let data;
        if (!isOutlook) {
          data = await getGmailEmailsFromDB(
            { ...emailfilter, page: currentPage, page_size: emailsPerPage },
            userGoogleId,
          );
        } else if (isOutlook) {
          data = await getOutlookEmailsFromDB(
            { ...emailfilter, page: currentPage, page_size: emailsPerPage },
            userGoogleId,
          );
        }

        if (isActive) {
          setEmails(data?.emails || []);
          setOriginalEmailsOrder(data?.emails || []);
          setEmailStats({ ...data, emails: [] });
          setTotalEmails(data?.total_count || 0);
        }

        const newData = {
          emails: data?.emails || [],
          totalEmails: data?.total_count || 0,
          emailStats: { ...data, emails: [] },
        };

        // Evict the least recently used entry if cache exceeds MAX_KEYS.
        if (emailCacheRef.current.size >= MAX_KEYS) {
          const lruKey = emailCacheRef.current.keys().next().value;
          emailCacheRef.current.delete(lruKey);
        }
        // Insert the new entry into the cache.
        emailCacheRef.current.set(cacheKey, {
          data: newData,
          timestamp: Date.now(),
        });
      } catch (error) {
        if (isActive) {
          // toast.error("Error: " + error.message);
        }
      } finally {
        if (isActive) setLoadingEmails(false);
      }
    };

    fetchEmails();

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, emailfilter, isOutlook, refreshKey, isSearchDone]);

  const handleEmailNotification = (data) => {
    const { email, status, appname } = data;
    if (status === 'new') {
      // email = {message_id: <some_id>, ...other fields}
      if (!seenMessageIdsRef.current.has(email.message_id)) {
        // first time seeing this message_id
        seenMessageIdsRef.current.add(email.message_id);
        const message =
          'Got new email in ' + (appname === 'gmail' ? 'Gmail' : 'Outlook');

        toast.success(message);
        setNewEmailCount((count) => count + 1);

        if (email.priority !== undefined && email.priority !== null) {
          setPriorities((prev) => [...prev, email.priority]); // Add the priority
        }
      } else {
      }
    } else if (status === 'deleted') {
      const message =
        'Deleted an email from ' + (appname === 'gmail' ? 'Gmail' : 'Outlook');
      // email = {message_id: <some_id>}
      toast.success(message);
    }
    // 1) wipe out cached pages so stale entries never win
    emailCacheRef.current.clear();

    // 2) force the useEffect to re‐run immediately
    setRefreshKey((k) => k + 1);
  };

  useEffect(() => {
    if (!isConnected) return;
    const eventKey = 'email_notification';

    // Subscribe to the WebSocket event.
    const unsubscribe = subscribe({
      [eventKey]: (data) => {
        // toast.success(JSON.stringify(data));
        handleEmailNotification(data);
      },
    });

    // Cleanup on unmount or dependency change.
    return () => {
      unsubscribe();
    };
  }, [subscribe, isConnected]);

  const clearCacheByOutlookFlag = (isOutlookToClear) => {
    for (let key of emailCacheRef.current.keys()) {
      try {
        const parsedKey = JSON.parse(key);
        if (parsedKey.isOutlook === isOutlookToClear) {
          emailCacheRef.current.delete(key);
        }
      } catch (error) {}
    }
  };

  useEffect(() => {
    if (gmailClearStatus.status) {
      clearCacheByOutlookFlag(false);
      setGmailClearStatus((prev) => ({ ...prev, status: false }));

      // setGmailSyncStatus((prev) => ({
      //   status: false,
      //   state: prev.state + 1,
      // }));
      setIsGmailSynchronized(false);

      // Reset immediately
      // setEmails([]);
      // setOriginalEmailsOrder([]);
      // setEmailStats(null);
      // setTotalEmails(0);

      setRefreshKey((k) => k + 1);
    }
    // eslint-disable-next-line
  }, [gmailClearStatus.state]);

  useEffect(() => {
    if (outlookClearStatus.status) {
      clearCacheByOutlookFlag(true);
      setOutlookClearStatus((prev) => ({ ...prev, status: false }));
      // setOutlookSyncStatus((prev) => ({
      //   status: false,
      //   state: prev.state + 1,
      // }));

      setIsOutlookSynchronized(false);

      // Reset immediately
      // setEmails([]);
      // setOriginalEmailsOrder([]);
      // setEmailStats(null);
      // setTotalEmails(0);

      setRefreshKey((k) => k + 1);
    }
    // eslint-disable-next-line
  }, [outlookClearStatus.state]);

  useEffect(() => {
    if (gmailAuthStatus.status) {
      setIsGmailAuthenticated(true);
      // Reset immediately
      // setEmails([]);
      // setOriginalEmailsOrder([]);
      // setEmailStats(null);
      // setTotalEmails(0);
      setIsOutlook(false);
    } else if (!gmailAuthStatus.status) {
      setIsGmailAuthenticated(false);
      clearCacheByOutlookFlag(false);
      setIsOutlook(true);
    }
    setForceRefetch(true);

    setRefreshKey((k) => k + 1);
    // eslint-disable-next-line
  }, [gmailAuthStatus.state]);

  useEffect(() => {
    if (outlookAuthStatus.status) {
      setIsOutlookAuthenticated(true);
      // Reset immediately
      // setEmails([]);
      // setOriginalEmailsOrder([]);
      // setEmailStats(null);
      // setTotalEmails(0);
      setIsOutlook(true);
    } else if (!outlookAuthStatus.status) {
      setIsOutlookAuthenticated(false);
      clearCacheByOutlookFlag(true);
      setIsOutlook(false);
    }
    setForceRefetch(true);

    setRefreshKey((k) => k + 1);
    // eslint-disable-next-line
  }, [outlookAuthStatus.state]);

  return (
    <EmailsContext.Provider
      value={{
        emails,
        setEmails,
        totalEmails,
        setTotalEmails,
        currentPage,
        setCurrentPage,
        emailfilter,
        setEmailFilter,
        loadingEmails,
        setLoadingEmails,
        originalEmailsOrder,
        setOriginalEmailsOrder,
        emailStats,
        setEmailStats,
        emailsPerPage,
        totalPages,
        setTotalPages,
        isOutlook,
        setIsOutlook,
        isGmailAuthenticated,
        isOutlookAuthenticated,
        isGmailSynchronized,
        isOutlookSynchronized,
        isSyncing,
        setIsSyncing,
        setIsSearchDone,
        newEmailCount,
        setNewEmailCount,
        priorities,
        setPriorities,
        seenMessageIdsRef,

        gmailClearStatus,
        setGmailClearStatus,
        outlookClearStatus,
        setOutlookClearStatus,

        gmailSyncStatus,
        setGmailSyncStatus,
        outlookSyncStatus,
        setOutlookSyncStatus,
        setForceRefetch,

        gmailAuthStatus,
        setGmailAuthStatus,
        outlookAuthStatus,
        setOutlookAuthStatus,
        setRefreshKey,
        refreshDialogKey,
        setRefreshDialogKey,
      }}
    >
      {children}
    </EmailsContext.Provider>
  );
};

export const useEmails = () => useContext(EmailsContext);
